/**
 * Whop's embedded checkout, as the two decisions worth testing.
 *
 * Whop presents one checkout configuration two ways, and on 2026-08-23 they
 * stopped agreeing. The hosted page at `whop.com/checkout/<id>` answers every
 * setup-mode configuration with `setup_mode_unsupported: setup sessions are not
 * yet available` - including `ch_VKGNU6BM56r6wAk`, which really did vault a card
 * on 2026-08-18. The embed renders the same id as a full card form. Both checked
 * against the live company the same afternoon.
 *
 * So the card form runs on the embed, and the hosted URL is a fallback for
 * whenever Whop puts it back.
 *
 * @module web/screens/app/whopEmbed
 */

/** Whop's loader. It scans the DOM for `data-whop-checkout-*` and mounts an iframe. */
export const WHOP_LOADER_SRC = "https://js.whop.com/static/checkout/loader.js";

/**
 * The attributes that make a div into a card form.
 *
 * `setup-future-usage=off_session` is the one that is easy to leave out and
 * expensive to miss: it is what tells Whop the card must be chargeable later
 * with nobody present, which is the entire point of vaulting. It reaches the
 * iframe as a query parameter (observed on the mounted frame), so it is checkable
 * rather than assumed.
 *
 * Only attributes verified against a real mount are here. Whop documents others
 * (styling, theme); none of them are guessed at from the docs alone.
 */
export function whopEmbedAttributes(sessionId: string, returnUrl: string): Record<string, string> {
  return {
    "data-whop-checkout-session": sessionId,
    "data-whop-checkout-setup-future-usage": "off_session",
    "data-whop-checkout-return-url": returnUrl,
  };
}

/**
 * The slice of `document` this needs.
 *
 * Declared rather than taking `Document` so the decision below can be tested
 * without a DOM. `web/` runs vitest in node on purpose - CLAUDE.md: pure logic
 * beside the screen, no second e2e stack - and real mounting is the smoke
 * test's job, in a real browser. A real `Document` satisfies this structurally.
 */
export interface LoaderHost {
  head: { appendChild(node: unknown): void };
  querySelectorAll(selector: string): ArrayLike<{ remove(): void }>;
  createElement(tag: string): { src: string; async: boolean; defer: boolean };
}

/**
 * Put the loader on the page so it scans, and make it scan AGAIN on a remount.
 *
 * The loader is built for static pages: it runs once and mounts whatever
 * `data-whop-checkout-*` elements exist at that moment. Ours appears when
 * somebody taps "Add card", which is always after first load, and it can appear
 * a second time if they close the form and reopen it. A plain "inject once if
 * absent" therefore mounts nothing the second time - the script is already
 * there, and nothing tells it to look again.
 *
 * Replacing the script element re-runs it, which re-scans. The target div must
 * already be in the DOM when this is called, so callers run it from an effect
 * rather than during render.
 */
export function mountWhopLoader(doc: LoaderHost = document): void {
  const existing = doc.querySelectorAll(`script[src="${WHOP_LOADER_SRC}"]`);
  for (let i = 0; i < existing.length; i++) existing[i]?.remove();

  const script = doc.createElement("script");
  script.src = WHOP_LOADER_SRC;
  script.async = true;
  script.defer = true;
  doc.head.appendChild(script);
}
